/**
 * Shared geo-location search helpers.
 *
 * Three surfaces in the app let users pick a geographic community
 * (Project Settings → Basics, Account Settings → Profile,
 * Community Settings → General). Earlier each had its own copy of
 * the search logic, which drifted: Project Settings had the
 * canonical scored algorithm, Account Settings had a weaker
 * `name.includes()` filter that under-ranked relevant matches and
 * fell back to displaying raw GUIDs in the chip, and Community
 * Settings had no typeahead at all — just a raw GUID input.
 *
 * This module is the single source of truth for:
 *   - What counts as a "geographic" entity (entity_type starts with 'geo_')
 *   - How user queries get scored against the geo index
 *   - How a stored GUID gets resolved to a human display label
 *
 * The companion `<GeoLocationPicker>` component in
 * `src/Components/GeoLocationPicker/` consumes these helpers and
 * provides the chip + input + suggestions UI. Most callers should use
 * the component; the helpers are exported for callers that need their
 * own custom UI but want consistent search behavior (e.g.,
 * ProjectConfigModal, which has bespoke CSS-module chrome).
 */

import { pathFromLeafGuid, pathDisplay } from './entityPath';

/* ── Types ────────────────────────────────────────────────────── */

export interface GeoLocationSuggestion {
  guid: string;
  display: string;
}

export interface GeoLocationSearchInputs {
  query: string;
  /** All community entities (geo + org + community). Filtered to geo internally. */
  entities: any[];
  /** Parent/child community relationships used to build path display. */
  relationships: any[];
  /** Max suggestions to return. Default 12 to match Project Settings. */
  limit?: number;
}

/* ── Predicates ───────────────────────────────────────────────── */

/**
 * True when an entity is a geographic community (city, region, country,
 * etc.) — distinct from organizations (Rotary clubs, NGOs) and other
 * community entity types that share the same table. The "Home Community"
 * and "Operational Community" fields are about LOCATIONS, not affiliations,
 * so they should only allow geo entities.
 *
 * The convention `entity_type` starts with `geo_` is established
 * elsewhere in the codebase (UserProfilePage, SignalDetectionPanel,
 * the inline checks that previously lived in each picker).
 */
export function isGeoEntity(entity: any): boolean {
  return String(entity?.entity_type ?? '').toLowerCase().startsWith('geo_');
}

/* ── Search ───────────────────────────────────────────────────── */

/**
 * Score and sort geographic suggestions against a user query.
 *
 * Scoring tiers (higher = more relevant):
 *   100 — leaf name starts with the query ("Boston" matches "bost")
 *    90 — full search string starts with the query
 *    70 — full search string contains " query" (word-boundary match —
 *         "Massachusetts" matches "Boston, Massachusetts, USA" because
 *         the second word starts with the query)
 *    50 — full search string just contains the query (substring fallback)
 *
 * Plus a small depth boost (up to +20) so deeper paths outrank
 * ambiguous shallow ones at the same score tier. A query like
 * "United States" matching just "United States" the country has the
 * same score as a city in it; the depth boost ensures the city ranks
 * higher because it carries more information.
 */
export function searchGeoLocations(
  inputs: GeoLocationSearchInputs,
): GeoLocationSuggestion[] {
  const q = String(inputs.query ?? '').trim().toLowerCase();
  if (!q) return [];
  const limit = inputs.limit ?? 12;

  const scored: { guid: string; display: string; score: number }[] = [];
  for (const e of inputs.entities) {
    if (!isGeoEntity(e)) continue;
    const guid = e?.entity_guid;
    if (!guid) continue;
    const leafName = String(e?.name ?? '').trim();
    if (!leafName) continue;

    // pathFromLeafGuid returns EntityLike[] in Root → Leaf order. The
    // canonical display in this app is Leaf → Root ("Boston, Massachusetts,
    // USA"), so reverse and project to names. pathDisplay takes string[],
    // not the raw entity array — passing entities directly produces a
    // stringified "[object Object], [object Object], …" chain.
    const rootToLeaf = pathFromLeafGuid(guid, inputs.entities as any, inputs.relationships as any);
    const parts = (rootToLeaf ?? [])
      .map((p: any) => String(p?.name ?? '').trim())
      .filter(Boolean);
    const display = pathDisplay(parts.reverse());
    if (!display) continue;

    const search = `${leafName} ${display}`.toLowerCase();
    let score = -1;
    if (leafName.toLowerCase().startsWith(q)) score = 100;
    else if (search.startsWith(q)) score = 90;
    else if (search.includes(` ${q}`)) score = 70;
    else if (search.includes(q)) score = 50;
    if (score < 0) continue;

    const depthBoost = Math.min(20, display.split(',').length);
    scored.push({ guid, display, score: score + depthBoost });
  }

  scored.sort((a, b) => b.score - a.score || a.display.localeCompare(b.display));
  return scored.slice(0, limit).map((x) => ({ guid: x.guid, display: x.display }));
}

/* ── Label resolution ──────────────────────────────────────────── */

/**
 * Resolve a stored geo location GUID to its display label.
 *
 * Returns:
 *   - `''` (empty) when guid is empty/null OR entities haven't loaded yet
 *     — caller should rely on its own placeholder copy in this state.
 *   - `'Unknown community'` when guid is set but no matching entity exists
 *     (e.g., entity was deleted). Earlier code returned the raw GUID here,
 *     which leaked uuid strings into chips and labels — bad UX.
 *   - The full path display otherwise.
 */
export function getGeoLocationLabel(args: {
  guid: string | null | undefined;
  entities: any[];
  relationships: any[];
}): string {
  if (!args.guid) return '';
  if (!args.entities || args.entities.length === 0) return '';
  const guidUpper = args.guid.toUpperCase();
  const entity = args.entities.find(
    (e) => String(e?.entity_guid ?? '').toUpperCase() === guidUpper,
  );
  if (!entity) return 'Unknown community';
  // Same Root → Leaf reversal as searchGeoLocations above. pathDisplay
  // takes string[] only.
  const rootToLeaf = pathFromLeafGuid(args.guid, args.entities as any, args.relationships as any);
  const parts = (rootToLeaf ?? [])
    .map((p: any) => String(p?.name ?? '').trim())
    .filter(Boolean);
  return pathDisplay(parts.reverse());
}
